import session from '@/utils/session.js'
import cache from '@/utils/cache/index.js'

const DEFAULT_API_PATH = '/sdk/api/customer/ticket'

export function buildSupportApiBase() {
  const queryBase = readParam('apiBase')
  if (queryBase) return queryBase.replace(/\/$/, '')
  return `${process.env.VUE_APP_SUPPORT_URL}/meow-customer${DEFAULT_API_PATH}`
}

export function buildSupportSession({ roleInfo = {}, gameData = {}, loginData = {} } = {}) {
  const userId = session.getUserId() || loginData.userId || loginData.id || ''
  return {
    userId,
    gameCode: cache.getGameCode() || gameData.gameCode || '',
    gameName: gameData.gameName || '',
    serverCode: roleInfo.serverCode || '',
    serverName: roleInfo.serverName || '',
    roleId: roleInfo.roleId || '',
    roleName: roleInfo.roleName || '',
    roleLevel: roleInfo.roleLevel || '',
    platform: loginData.platform || loginData.channel || 'meow',
    packageName: loginData.packageName || '',
    loginToken: session.getLoginToken() || loginData.loginToken || loginData.token || '',
    loginAccessToken: session.getLoginToken() || loginData.loginAccessToken || loginData.token || '',
    loginTimestamp: loginData.loginTimestamp || loginData.timestamp || '',
    language: normalizeLanguage(readLanguageParam() || gameData.gameLanguage || loginData.language || navigator.language),
    theme: readParam('theme') || cache.getGameCode() || '',
    osVersion: loginData.osVersion || '',
    device: loginData.device || ''
  }
}

export function hasSupportSession(data) {
  return !!(data && data.userId && data.gameCode && data.roleId && data.serverCode)
}

export function normalizeLanguage(value) {
  const language = String(value || '').toLowerCase().replace('_', '-')
  if (language.indexOf('ko') === 0) return 'ko_KR'
  if (language.indexOf('ru') === 0) return 'ru_RU'
  if (language.indexOf('vi') === 0) return 'vi_VN'
  if (language.indexOf('th') === 0) return 'th_TH'
  if (language.indexOf('zh-tw') === 0 || language.indexOf('tw') === 0) return 'zh_TW'
  if (language.indexOf('zh') === 0) return 'zh_CH'
  if (language.indexOf('en') === 0) return 'en_US'
  return 'zh_TW'
}

export function normalizeUserId(value) {
  const num = Number(value)
  return Number.isFinite(num) ? num : value
}

export async function supportRequest(path = '', options = {}) {
  const opts = Object.assign({
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  }, options)
  const supportSession = opts.session || {}
  delete opts.session

  opts.headers = Object.assign({}, opts.headers || {}, authHeaders(supportSession))
  if (opts.body && typeof opts.body !== 'string') {
    opts.body = JSON.stringify(opts.body)
  }

  const response = await fetch(`${buildSupportApiBase()}${path}`, opts)
  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText}`)
  }

  const data = await response.json()
  if (data.result === undefined && data.data !== undefined) {
    data.result = data.data
  }
  if (
    data.success === false ||
    data.successful === false ||
    (data.code && [0, 200, 1000].indexOf(data.code) < 0)
  ) {
    throw new Error(data.message || '')
  }
  return data
}

export function getTicketList(supportSession, pageNo, pageSize) {
  const query = toQuery({
    pageNo,
    pageSize,
    userId: supportSession.userId,
    gameCode: supportSession.gameCode,
    serverCode: supportSession.serverCode,
    roleId: supportSession.roleId
  })
  return supportRequest(`/list?${query}`, { session: supportSession })
}

export function createTicket(supportSession, payload) {
  return supportRequest('', {
    method: 'POST',
    session: supportSession,
    body: Object.assign({}, supportSession, payload, {
      userId: normalizeUserId(supportSession.userId),
      source: 'GAME',
      language: normalizeLanguage(supportSession.language)
    })
  })
}

export function getTicketDetail(supportSession, ticketId) {
  return supportRequest(`/${encodeURIComponent(ticketId)}?${toQuery({ userId: supportSession.userId })}`, {
    session: supportSession
  })
}

export function markTicketRead(supportSession, ticketId) {
  return supportRequest('/read', {
    method: 'POST',
    session: supportSession,
    body: {
      ticketId,
      userId: supportSession.userId,
      gameCode: supportSession.gameCode,
      serverCode: supportSession.serverCode,
      roleId: supportSession.roleId
    }
  })
}

export function replyTicket(supportSession, ticket, payload) {
  const ticketId = ticket && (ticket.id || ticket.ticketId || ticket.aqId)
  return supportRequest('/reply', {
    method: 'POST',
    session: supportSession,
    body: {
      ticketId,
      userId: supportSession.userId,
      messageType: 'PLAYER_REPLY',
      content: payload.content || payload.attachmentFallback || '',
      attachments: payload.attachments || '',
      senderType: 'PLAYER',
      senderName: supportSession.roleName || String(supportSession.userId || ''),
      visibleToPlayer: 1
    }
  })
}

export function markTicketResolved(supportSession, ticket) {
  const id = ticket && (ticket.id || ticket.ticketId || ticket.aqId)
  return supportRequest('/status', {
    method: 'POST',
    session: supportSession,
    body: {
      id,
      userId: supportSession.userId,
      status: 'RESOLVED',
      operatorName: supportSession.roleName || String(supportSession.userId || '')
    }
  })
}

export function rateTicket(supportSession, ticket, score) {
  const id = ticket && (ticket.id || ticket.ticketId || ticket.aqId)
  return supportRequest('/satisfaction', {
    method: 'POST',
    session: supportSession,
    body: {
      id,
      userId: supportSession.userId,
      satisfactionScore: score,
      operatorName: supportSession.roleName || String(supportSession.userId || '')
    }
  })
}

export function unwrapResult(response) {
  return (response && (response.result || response.data)) || response || {}
}

export function toQuery(data) {
  return Object.keys(data)
    .filter(key => data[key] !== undefined && data[key] !== null && data[key] !== '')
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
    .join('&')
}

function authHeaders(supportSession) {
  return {
    'X-User-Id': supportSession.userId || session.getUserId() || '',
    'X-Game-Code': supportSession.gameCode || cache.getGameCode() || '',
    'X-Auth-Token': supportSession.loginToken || supportSession.loginAccessToken || session.getLoginToken() || '',
    'X-Login-Timestamp': supportSession.loginTimestamp || ''
  }
}

function readLanguageParam() {
  return readParam('gameLanguage') ||
    readParam('game_language') ||
    readParam('gameLang') ||
    readParam('language') ||
    readParam('lang') ||
    readParam('locale')
}

function readParam(name) {
  const sources = [
    window.location.search,
    window.location.hash,
    window.location.href
  ]
  try {
    sources.push(decodeURIComponent(window.location.href))
  } catch (error) {}

  for (let i = 0; i < sources.length; i += 1) {
    const value = readParamFromText(sources[i], name)
    if (value) return value
  }
  return ''
}

function readParamFromText(text, name) {
  if (!text) return ''
  let query = String(text)
  const queryIndex = query.indexOf('?')
  if (queryIndex >= 0) query = query.slice(queryIndex + 1)
  query = query.replace(/^#/, '')
  const parts = query.split(/[&#]/)
  const lowerName = String(name).toLowerCase()
  for (let i = 0; i < parts.length; i += 1) {
    const pair = parts[i].split('=')
    if (decodeURIComponent(pair[0] || '').toLowerCase() === lowerName) {
      return decodeURIComponent((pair.slice(1).join('=') || '').replace(/\+/g, ' '))
    }
  }
  return ''
}
